/**
 * NotificationsModal — chronological feed of the bell icon.
 *
 * Pulls /api/me/notifications with cursor pagination. Each row
 * renders type-specific copy + a deep-link affordance. Row click
 * marks the row read AND fires the deep-link callback the parent
 * provided.
 *
 * Mark-all-as-read button bulk-clears unread state — useful when
 * the user has been away and the feed is full of stale events they
 * don't want to scroll through.
 */
import { useState, useEffect, useCallback } from "react";
import { api } from "../api.js";
import { T_NEW } from "../theme.js";
import { Modal, Mono, Btn } from "./ui.jsx";
import { useT, plural } from "../i18n.jsx";
import {
  isPushSupported, permissionState, subscribeForPush,
} from "../pushSubscription.js";

const STATUS_KEYS = {
  confirmed:   "notifications.status.confirmed",
  received:    "notifications.status.received",
  shipped:     "notifications.status.shipped",
  delivered:   "notifications.status.delivered",
  cancelled:   "notifications.status.cancelled",
  unavailable: "notifications.status.unavailable",
};

const TYPE_RENDERERS = {
  order_status_changed: (p, t) => ({
    title: t("notifications.type.order", { status: STATUS_KEYS[p.status] ? t(STATUS_KEYS[p.status]) : p.status }),
    body: p.postTitle || "",
    icon: "📦",
    deepLink: { kind: "orders" },
  }),
  balance_credited: (p, t) => ({
    title: t("notifications.type.credit", { amt: ((p.amountCents || 0) / 100).toFixed(2) }),
    body: p.source === "referral_bonus" ? t("notifications.type.credit.referral") : t("notifications.type.credit.cashback"),
    icon: "$",
    deepLink: { kind: "balance" },
  }),
  customer_ordered: (p, t) => ({
    title: t("notifications.type.customerOrdered", { name: p.customerName || t("notifications.type.customerOrdered.unknown") }),
    body: p.postTitle || "",
    icon: "🛍",
    deepLink: { kind: "orders" },
  }),
  payment_requested: (p, t) => ({
    title: t("notifications.type.paymentRequested", { name: p.customerName || t("notifications.type.customerOrdered.unknown") }),
    body: p.postTitle || "",
    icon: "💳",
    deepLink: p.dmId ? { kind: "dm", dmId: p.dmId } : { kind: "chats" },
  }),
  payment_uploaded: (p, t) => ({
    title: t("notifications.type.paymentUploaded", { name: p.customerName || t("notifications.type.customerOrdered.unknown") }),
    body: p.postTitle || "",
    icon: "🧾",
    deepLink: { kind: "orders" },
  }),
  forward_completed: (p, t) => {
    const noun = t(plural(p.postCount || 0, "notifications.type.forward.cards"));
    const okPart = p.succeeded
      ? t("notifications.type.forward.ok", { n: p.succeeded })
      : t("notifications.type.forward.none");
    return {
      title: t("notifications.type.forward", { n: p.postCount, noun }),
      body: okPart + (p.failed
        ? t("notifications.type.forward.failed", { n: p.failed })
        : ""),
      icon: "↗",
      deepLink: null,    // forward history is in MyStore stats
    };
  },
  reminder_digest: (p, t) => {
    const names = (p.sampleNames || []).slice(0, 3).join(", ");
    return {
      title: t("notifications.type.reminder", {
        count: p.count,
        suffix: p.count === 1 ? t("notifications.type.reminder.suffix.one") : t("notifications.type.reminder.suffix.many"),
      }),
      body: names + (p.count > 3 ? t("notifications.type.reminder.andMore") : ""),
      icon: "🕐",
      deepLink: { kind: "customers" },
    };
  },
  customer_bot_linked: (p, t) => ({
    title: t("notifications.type.botLinked", { name: p.customerName || t("notifications.type.botLinked.unknown") }),
    body: t("notifications.type.botLinked.body"),
    icon: "✓",
    deepLink: { kind: "customers" },
  }),
  weekly_reengagement: (p, t) => ({
    title: t("notifications.type.reengage", { n: p.count || 0 }),
    body: (p.sampleNames || []).slice(0, 3).join(", "),
    icon: "★",
    deepLink: { kind: "customers" },
  }),
};

function bucketFor(unixSec) {
  const now = Date.now() / 1000;
  const ageSec = now - unixSec;
  if (ageSec < 86400) return "today";
  if (ageSec < 86400 * 7) return "week";
  return "older";
}
const BUCKET_KEY = {
  today: "notifications.bucket.today",
  week:  "notifications.bucket.week",
  older: "notifications.bucket.older",
};

export function NotificationsModal({
  vapidPublicKey,
  onClose,
  onOpenOrders,
  onOpenBalance,
  onOpenCustomers,
  onOpenDm,
  onChanged,
}) {
  const t = useT();
  // Push opt-in state. Default to default; subscribe button only shows
  // if the browser supports push AND permission isn't "granted" yet
  // AND VAPID is configured.
  const [pushPerm, setPushPerm] = useState(
    typeof window !== "undefined" ? permissionState() : "default",
  );
  const [pushBusy, setPushBusy] = useState(false);
  const canOfferPush =
    isPushSupported() &&
    vapidPublicKey &&
    pushPerm !== "granted" &&
    pushPerm !== "denied";

  const handleEnablePush = async () => {
    setPushBusy(true);
    try {
      await subscribeForPush(vapidPublicKey);
      setPushPerm(permissionState());
    } catch {
      setPushPerm(permissionState());
    } finally {
      setPushBusy(false);
    }
  };
  const [items, setItems] = useState(null);     // null = loading
  const [cursor, setCursor] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [markingAll, setMarkingAll] = useState(false);

  const loadFirstPage = useCallback(async () => {
    try {
      const r = await api.listNotifications({ limit: 30 });
      setItems(r.items || []);
      setCursor(r.nextCursor || null);
    } catch {
      setItems([]);
    }
  }, []);

  useEffect(() => { loadFirstPage(); }, [loadFirstPage]);

  const loadMore = async () => {
    if (!cursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const r = await api.listNotifications({ cursor, limit: 30 });
      setItems((prev) => [...(prev || []), ...(r.items || [])]);
      setCursor(r.nextCursor || null);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleRowClick = async (n) => {
    if (!n.readAt) {
      try {
        await api.markNotificationsRead({ ids: [n.id] });
        setItems((prev) => prev.map((x) =>
          x.id === n.id
            ? { ...x, readAt: Math.floor(Date.now() / 1000) }
            : x,
        ));
        onChanged?.();
      } catch {}
    }
    const renderer = TYPE_RENDERERS[n.type];
    const dl = renderer ? renderer(n.payload || {}, t).deepLink : null;
    if (!dl) return;
    if (dl.kind === "orders") { onClose(); onOpenOrders?.(); }
    else if (dl.kind === "balance") { onClose(); onOpenBalance?.(); }
    else if (dl.kind === "customers") { onClose(); onOpenCustomers?.(); }
    else if (dl.kind === "dm" && dl.dmId) { onClose(); onOpenDm?.(dl.dmId); }
    else if (dl.kind === "chats") { onClose(); onOpenDm?.(null); }
  };

  const markAll = async () => {
    setMarkingAll(true);
    try {
      await api.markNotificationsRead({ all: true });
      setItems((prev) =>
        (prev || []).map((x) => ({
          ...x,
          readAt: x.readAt ?? Math.floor(Date.now() / 1000),
        })),
      );
      onChanged?.();
    } finally {
      setMarkingAll(false);
    }
  };

  // Group rendering — only emit a bucket header the first time we
  // cross into it.
  const renderFeed = () => {
    if (items === null) {
      return <p style={{ color: T_NEW.ink56, padding: 16 }}>{t("notifications.loading")}</p>;
    }
    if (items.length === 0) {
      return (
        <div style={{ padding: "32px 12px", textAlign: "center",
                      color: T_NEW.ink56 }}>
          <p style={{ marginBottom: 6, fontSize: T_NEW.body,
                       color: T_NEW.ink, fontWeight: 600 }}>
            {t("notifications.empty.title")}
          </p>
          <p style={{ fontSize: T_NEW.meta, lineHeight: 1.5, margin: 0 }}>
            {t("notifications.empty.body")}
          </p>
        </div>
      );
    }
    const rows = [];
    let lastBucket = null;
    for (const n of items) {
      const bucket = bucketFor(n.createdAt);
      if (bucket !== lastBucket) {
        rows.push(
          <Mono key={`b-${bucket}-${n.id}`} color={T_NEW.ink56}
                style={{ display: "block", margin: "12px 4px 6px",
                         fontWeight: 700 }}>
            {t(BUCKET_KEY[bucket])}
          </Mono>,
        );
        lastBucket = bucket;
      }
      rows.push(<NotificationRow key={n.id} n={n} onClick={handleRowClick} t={t} />);
    }
    return rows;
  };

  return (
    <Modal title={t("notifications.title")} onClose={onClose} width={480}>
      {canOfferPush && (
        <div style={{
          padding: "10px 12px", marginBottom: 12,
          background: T_NEW.persimmonSoft,
          border: `1px solid ${T_NEW.persimmon}33`,
          borderRadius: T_NEW.r1,
          display: "flex", alignItems: "center", gap: 10,
        }}>
          <div style={{ flex: 1, fontSize: T_NEW.meta,
                        color: T_NEW.ink, lineHeight: 1.4 }}>
            <strong>{t("notifications.push.title")}</strong>{" "}
            {t("notifications.push.body")}
          </div>
          <Btn variant="accent" onClick={handleEnablePush}
               disabled={pushBusy}>
            {pushBusy ? "…" : t("notifications.push.enable")}
          </Btn>
        </div>
      )}
      <div style={{ display: "flex", justifyContent: "space-between",
                    alignItems: "center", marginBottom: 8 }}>
        <Mono color={T_NEW.ink56}>{t("notifications.feed.label")}</Mono>
        <button
          onClick={markAll}
          disabled={markingAll || (items || []).every((x) => x.readAt)}
          style={{
            background: "transparent", border: "none",
            color: T_NEW.persimmon,
            fontFamily: T_NEW.fontMono, fontSize: T_NEW.micro,
            letterSpacing: T_NEW.monoTrack, textTransform: "uppercase",
            fontWeight: 700, cursor: "pointer", padding: 0,
            opacity: markingAll ? 0.5 : 1,
          }}>
          {t("notifications.markAll")}
        </button>
      </div>
      <div style={{ minHeight: 200 }}>
        {renderFeed()}
        {cursor && (
          <div style={{ textAlign: "center", marginTop: 12 }}>
            <Btn variant="ghost" onClick={loadMore} disabled={loadingMore}>
              {loadingMore ? t("notifications.loadingMore") : t("notifications.loadMore")}
            </Btn>
          </div>
        )}
      </div>
    </Modal>
  );
}

function NotificationRow({ n, onClick, t }) {
  const renderer = TYPE_RENDERERS[n.type];
  const r = renderer ? renderer(n.payload || {}, t) : {
    title: n.type, body: "", icon: "·", deepLink: null,
  };
  const dt = new Date(n.createdAt * 1000);
  const ageMin = (Date.now() / 1000 - n.createdAt) / 60;
  const timeText =
    ageMin < 60 ? `${Math.max(0, Math.floor(ageMin))} ${t("notifications.row.unit.min")}`
    : ageMin < 1440 ? `${Math.floor(ageMin / 60)} ${t("notifications.row.unit.hour")}`
    : dt.toLocaleDateString("ru-RU",
        { day: "2-digit", month: "2-digit" });
  return (
    <div
      onClick={() => onClick(n)}
      style={{
        display: "flex", alignItems: "flex-start", gap: 10,
        padding: "10px 12px",
        background: n.readAt ? "transparent" : T_NEW.persimmonSoft,
        border: `1px solid ${T_NEW.ink12}`,
        borderRadius: T_NEW.r1,
        marginBottom: 6,
        cursor: r.deepLink ? "pointer" : "default",
      }}>
      <div style={{
        width: 28, height: 28, flexShrink: 0,
        background: T_NEW.paperRaised,
        border: `1px solid ${T_NEW.ink12}`,
        borderRadius: 999,
        display: "flex", alignItems: "center", justifyContent: "center",
        fontSize: 14, fontWeight: 700, color: T_NEW.persimmon,
      }}>
        {r.icon}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: T_NEW.meta, color: T_NEW.ink,
                      fontWeight: 600, lineHeight: 1.35 }}>
          {r.title}
        </div>
        {r.body && (
          <Mono color={T_NEW.ink56} size={T_NEW.micro}
                style={{ display: "block", marginTop: 2,
                         whiteSpace: "nowrap", overflow: "hidden",
                         textOverflow: "ellipsis" }}>
            {r.body}
          </Mono>
        )}
      </div>
      <Mono color={T_NEW.ink38} size={T_NEW.micro}
            style={{ flexShrink: 0, marginTop: 2 }}>
        {timeText}
      </Mono>
    </div>
  );
}
